// ── Navigation bounds ─────────────────────────────────────────────
// Single source of truth for which sidebar steps are clickable.

import { getState, setState } from './state.js';

function hasListingDraft() {
  const title = getState('listingDraftTitle');
  if (title && !title.error && (title.titleEn || title.titleOptions?.length)) return true;
  const bullets = getState('listingDraftBullets');
  if (bullets && !bullets.error && bullets.bulletsEn?.length) return true;
  const desc = getState('listingDraftDescription');
  if (desc && !desc.error && desc.descriptionEn) return true;
  return false;
}

function hasKeywordLibraryReady() {
  return (getState('keywordLibrary')?.keywords?.length || 0) > 0;
}

/**
 * An active Listing task can be restored only through an explicit workbench
 * return or saved Listing action. Keep Step 03 reachable while that state is
 * rejoined; a plain browser refresh never supplies this anchor.
 */
function hasListingTaskRecoveryAnchor() {
  return Boolean(
    String(getState('batchJobId') || '').trim()
    && String(getState('activeListingGenerationTaskId') || '').trim(),
  );
}

function isStep1WorkDone() {
  const ready = getState('stepReady') || {};
  return !!(ready[2] || hasKeywordLibraryReady() || hasListingTaskRecoveryAnchor());
}

/** 批量分析完成后可进入 Step 3 预览竞品并触发生成（不必已有 Listing 草稿）。 */
function canEnterStep3() {
  const ready = getState('stepReady') || {};
  if (ready[3] || hasListingDraft() || hasListingTaskRecoveryAnchor()) return true;
  const snaps = getState('batchSnapshots');
  if (Array.isArray(snaps) && snaps.length) {
    return snaps.some((s) => s?.titleEn || (Array.isArray(s.bulletsEn) && s.bulletsEn.length));
  }
  const snap = getState('snapshotState');
  if (!getState('batchJobId') || !snap) return false;
  return !!(snap.titleEn || (Array.isArray(snap.bulletsEn) && snap.bulletsEn.length));
}

/** 侧栏「已完成」判定：与 getMaxNavigableStep 使用相同的数据条件，避免仅 stepReady 未置位时误显示「可查看」。 */
export function isStepCompleted(step) {
  const ready = getState('stepReady') || {};
  if (ready[step]) return true;

  switch (step) {
    case 1:
      return isStep1WorkDone();
    case 2:
      return hasKeywordLibraryReady() || hasListingTaskRecoveryAnchor();
    case 3:
      return hasListingDraft();
    case 4: {
      const compliance = getState('complianceResult');
      return !!(compliance && (compliance.violations !== undefined || compliance.score !== undefined));
    }
    default:
      return false;
  }
}

/** Max step the user may open from the sidebar or 「下一步」 (based on real data + stepReady). */
export function getMaxNavigableStep() {
  // 图片工坊复用内部 step-image 面板，不参与 Listing 四步侧栏。
  if (getState('workflowMode') === 'assets') return 7;

  const ready = getState('stepReady') || {};
  let max = 1;

  const unlock2 = ready[2] || hasKeywordLibraryReady() || hasListingTaskRecoveryAnchor();
  if (!unlock2) return max;

  max = 2;
  if (!canEnterStep3()) return max;

  max = 3;
  if (!(ready[3] || hasListingDraft())) return max;

  // 有 Listing 即可进入 Step4 执行合规（不必等 complianceResult）
  max = 4;
  return max;
}

/** Clamp highestStep / currentStep to data-backed limits (fixes stale localStorage). */
export function syncNavigationBounds() {
  // 图片工坊复用内部 step-image 面板，不参与 Listing 四步侧栏。
  if (getState('workflowMode') === 'assets') {
    if (getState('highestStep') !== 7) setState('highestStep', 7);
    if (getState('currentStep') !== 7) setState('currentStep', 7);
    return;
  }

  const maxNav = getMaxNavigableStep();
  const highest = getState('highestStep');
  if (highest !== maxNav) setState('highestStep', maxNav);

  const cur = getState('currentStep');
  if (cur > maxNav) setState('currentStep', maxNav);
}
